import { initializeApp } from "firebase/app";
import {
  GoogleAuthProvider,
  getAuth,
  signInWithPopup,
  signOut,
  onAuthStateChanged
} from "firebase/auth";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  getFirestore,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where
} from "firebase/firestore";
import {
  deleteObject,
  getDownloadURL,
  getStorage,
  ref,
  uploadBytesResumable
} from "firebase/storage";
import { initializeAppCheck, ReCaptchaEnterpriseProvider } from "firebase/app-check";

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID
};

export const isFirebaseConfigured = Boolean(
  firebaseConfig.apiKey && firebaseConfig.projectId && firebaseConfig.appId
);

const app = isFirebaseConfigured ? initializeApp(firebaseConfig) : null;
if (app && import.meta.env.VITE_FIREBASE_APPCHECK_SITE_KEY && typeof window !== "undefined") {
  initializeAppCheck(app, {
    provider: new ReCaptchaEnterpriseProvider(import.meta.env.VITE_FIREBASE_APPCHECK_SITE_KEY),
    isTokenAutoRefreshEnabled: true
  });
}
export const auth = app ? getAuth(app) : null;
export const db = app ? getFirestore(app) : null;
export const storage = app ? getStorage(app) : null;

export const observeAuth = (callback) => {
  if (!auth) {
    callback(null);
    return () => {};
  }
  return onAuthStateChanged(auth, callback);
};

export const loginAdminWithGoogle = () => {
  if (!auth) throw new Error("Firebase is not configured. Add the project values to .env.");
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ prompt: "select_account" });
  return signInWithPopup(auth, provider);
};

export const logoutAdmin = () => (auth ? signOut(auth) : Promise.resolve());

export const getAdminRecord = async (uid) => {
  if (!db || !uid) return null;
  const snapshot = await getDoc(doc(db, "admins", uid));
  return snapshot.exists() ? { id: snapshot.id, ...snapshot.data() } : null;
};

const fallbackManagedCollections = new Set([
  "slides",
  "pages",
  "announcements",
  "satsangPrograms",
  "events",
  "galleryAlbums",
  "dailyThoughts"
]);

export const getPublicDocuments = async (collectionName) => {
  if (!db) return null;
  const collectionRef = collection(db, collectionName);
  const publicQuery = collectionName === "websiteSettings"
    ? query(collectionRef, where("public", "==", true))
    : query(collectionRef, where("status", "==", "published"));
  const snapshot = await getDocs(publicQuery);
  const publishedItems = snapshot.docs.map((entry) => ({ id: entry.id, ...entry.data() }));
  if (!fallbackManagedCollections.has(collectionName)) return publishedItems;

  let replacementItems = [];
  try {
    const replacementSnapshot = await getDocs(query(collectionRef, where("replacesBundled", "==", true)));
    replacementItems = replacementSnapshot.docs.map((entry) => ({ id: entry.id, ...entry.data() }));
  } catch {
    // Published content remains available before the updated Firestore rules are deployed.
  }
  const byId = new Map([...publishedItems, ...replacementItems].map((item) => [item.id, item]));
  return [...byId.values()];
};

export const getAdminDocuments = async (collectionName) => {
  if (!db) return [];
  const snapshot = await getDocs(collection(db, collectionName));
  return snapshot.docs.map((entry) => ({ id: entry.id, ...entry.data() }));
};

export const saveAdminDocument = async (collectionName, values, userId, documentId) => {
  if (!db) throw new Error("Firebase is not configured.");
  const payload = {
    ...values,
    updatedAt: serverTimestamp()
  };
  if (documentId) {
    const documentRef = doc(db, collectionName, documentId);
    const existing = await getDoc(documentRef);
    if (existing.exists()) {
      await updateDoc(documentRef, payload);
    } else {
      await setDoc(documentRef, {
        ...payload,
        createdAt: serverTimestamp(),
        createdBy: userId
      });
    }
    return documentId;
  }
  const result = await addDoc(collection(db, collectionName), {
    ...payload,
    createdAt: serverTimestamp(),
    createdBy: userId
  });
  return result.id;
};

export const saveWebsiteSettings = async (values) => {
  if (!db) throw new Error("Firebase is not configured.");
  await setDoc(doc(db, "websiteSettings", "general"), {
    ...values,
    public: true,
    updatedAt: serverTimestamp()
  }, { merge: true });
};

export const removeAdminDocument = async (collectionName, documentId) => {
  if (!db) throw new Error("Firebase is not configured.");
  await deleteDoc(doc(db, collectionName, documentId));
};

export const submitContactMessage = async (values) => {
  if (!db) throw new Error("Online enquiries are being configured. Please contact the organisation directly.");
  return addDoc(collection(db, "contactMessages"), {
    fullName: values.fullName.trim(),
    email: values.email.trim().toLowerCase(),
    phone: values.phone.trim(),
    subject: values.subject.trim(),
    message: values.message.trim(),
    status: "unread",
    createdAt: serverTimestamp()
  });
};

const allowedTypes = {
  image: ["image/jpeg", "image/png", "image/webp", "image/gif"],
  audio: ["audio/mpeg", "audio/mp4", "audio/aac", "audio/wav", "audio/x-wav", "audio/ogg"],
  video: ["video/mp4", "video/webm", "video/quicktime", "video/x-m4v"],
  document: ["application/pdf", "image/jpeg", "image/png", "image/webp"]
};

export const uploadAsset = (file, folder, onProgress = () => {}) => {
  if (!storage) return Promise.reject(new Error("Firebase Storage is not configured."));
  const typeGroup = folder === "audio"
    ? "audio"
    : folder === "videos"
      ? "video"
      : folder === "documents"
        ? "document"
        : "image";
  const maxSize = typeGroup === "video" ? 500 : typeGroup === "audio" ? 100 : typeGroup === "document" ? 20 : 8;
  if (!allowedTypes[typeGroup].includes(file.type)) {
    return Promise.reject(new Error(`Unsupported ${typeGroup} file type.`));
  }
  if (file.size > maxSize * 1024 * 1024) {
    return Promise.reject(new Error(`File must be smaller than ${maxSize} MB.`));
  }

  const safeName = file.name.toLowerCase().replace(/[^a-z0-9._-]+/g, "-");
  const objectRef = ref(storage, `${folder}/${Date.now()}-${safeName}`);
  const task = uploadBytesResumable(objectRef, file, { contentType: file.type });

  return new Promise((resolve, reject) => {
    task.on(
      "state_changed",
      (snapshot) => onProgress(Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100)),
      reject,
      async () => resolve({ url: await getDownloadURL(task.snapshot.ref), path: task.snapshot.ref.fullPath })
    );
  });
};

export const removeStoredAsset = async (path) => {
  if (!storage || !path) return;
  await deleteObject(ref(storage, path));
};
